import React, { useMemo, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { toPoint, forward as toMGRS } from "mgrs";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Box,
  TextField,
  Button,
  Checkbox,
  Typography,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Stack,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import SendIcon from "@mui/icons-material/Send";
import {
  tacticalActionSx,
  tacticalCardSx,
  tacticalChipSx,
  tacticalMetricSx,
} from "../../utils/fdcTheme";

function parseMgrsAndAltitude(rawInput) {
  const raw = (rawInput || "").trim();
  if (!raw) return { ok: false, error: "Empty input." };

  const mgrsMatch = raw.match(/^([0-9]{1,2}[C-HJ-NP-X][A-Z]{2}[0-9]{2,})/i);

  if (!mgrsMatch) {
    return {
      ok: false,
      error: "Invalid MGRS format. Example: 29SND6264358593 167.53m msl:",
    };
  }

  const mgrsCandidate = mgrsMatch[1].toUpperCase();
  const mgrsNormalized = mgrsCandidate.replace(/\s+/g, "");

  const digitsPart = mgrsNormalized.replace(
    /^[0-9]{1,2}[C-HJ-NP-X][A-Z]{2}/i,
    "",
  );

  if (digitsPart.length % 2 !== 0) {
    return {
      ok: false,
      error:
        "MGRS digits must be even (equal easting/northing). Example: 29SND6264358593",
    };
  }

  let lon, lat;
  try {
    [lon, lat] = toPoint(mgrsNormalized);
  } catch {
    return {
      ok: false,
      error: "Unable to convert MGRS to coordinates. Verify grid reference.",
    };
  }

  const altMatch = raw.match(/(\d+(?:[.,]\d+)?)\s*m\b/i);
  let altitude = null;

  if (altMatch) {
    altitude = parseFloat(altMatch[1].replace(",", "."));
    if (!Number.isFinite(altitude)) altitude = null;
  }

  return {
    ok: true,
    mgrs: mgrsNormalized,
    lat,
    lon,
    altitude,
  };
}

function validateAltitudeMeters(altitude) {
  if (!Number.isFinite(altitude)) return "Altitude must be a number.";
  if (altitude < -500 || altitude > 9000) {
    return "Altitude looks out of range (-500 to 9000 m).";
  }
  return "";
}

const statusToneFromMessage = (msg) => {
  if (!msg) return "neutral";
  if (msg.startsWith("GPS OK")) return "success";
  if (/permission|timeout|error|unavailable/i.test(msg)) return "warning";
  return "neutral";
};

function BatteryForm({
  backendUrl,
  missionId,
  gunOptions = [],
  onSaved,
  fixedGunNumber = "",
  hideGunSelect = false,
}) {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    gun_number: "",
    is_battery_center: false,
    mgrs_input: "",
    altitude: "",
  });

  useEffect(() => {
    if (fixedGunNumber) {
      setFormData((p) => ({ ...p, gun_number: String(fixedGunNumber) }));
    }
  }, [fixedGunNumber]);

  const [parsed, setParsed] = useState({
    mgrsNormalized: "",
    lat: null,
    lon: null,
  });

  const [mgrsError, setMgrsError] = useState("");
  const [altitudeError, setAltitudeError] = useState("");
  const [result, setResult] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsMsg, setGpsMsg] = useState("");
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [takMarkers, setTakMarkers] = useState([]);
  const [takAnchor, setTakAnchor] = useState(null);
  const [takBusy, setTakBusy] = useState(false);

  const effectiveGunNumber = fixedGunNumber
    ? String(fixedGunNumber)
    : String(formData.gun_number || "");

  const canSubmit = useMemo(() => {
    const hasCore =
      !!effectiveGunNumber &&
      !!formData.mgrs_input &&
      parsed.lat != null &&
      parsed.lon != null &&
      formData.altitude !== "";

    return hasCore && !mgrsError && !altitudeError;
  }, [effectiveGunNumber, formData, parsed, mgrsError, altitudeError]);

  const clearErrors = useCallback(() => {
    setMgrsError("");
    setAltitudeError("");
    setSubmitError("");
    setGpsMsg("");
  }, []);

  const parseAndApply = useCallback(
    (rawInput, { autofillAltitude = true } = {}) => {
      clearErrors();
      const out = parseMgrsAndAltitude(rawInput);

      if (!out.ok) {
        setParsed({ mgrsNormalized: "", lat: null, lon: null });
        setMgrsError(out.error);
        return { ok: false };
      }

      setParsed({
        mgrsNormalized: out.mgrs,
        lat: out.lat,
        lon: out.lon,
      });

      if (autofillAltitude && out.altitude != null) {
        setFormData((p) => ({
          ...p,
          altitude: out.altitude.toFixed(2),
        }));
      }

      return { ok: true, altitudeFromText: out.altitude };
    },
    [clearErrors],
  );

  const handleMgrsBlur = () => {
    const rawInput = (formData.mgrs_input || "").trim();
    if (!rawInput) return;
    parseAndApply(rawInput, { autofillAltitude: true });
  };

  const handleUseGps = async () => {
    setResult("");
    clearErrors();

    if (!navigator?.geolocation) {
      setGpsMsg("Geolocation not supported on this device/browser.");
      return;
    }

    setGpsBusy(true);
    setGpsMsg("Getting GPS fix… enable location permissions.");
    setGpsAccuracy(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        try {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          const alt =
            typeof pos.coords.altitude === "number"
              ? pos.coords.altitude
              : null;
          const acc =
            typeof pos.coords.accuracy === "number"
              ? pos.coords.accuracy
              : null;

          const mgrs = toMGRS([lon, lat], 5);
          const mgrsText = alt != null ? `${mgrs} ${alt.toFixed(2)}m` : mgrs;

          setFormData((p) => ({
            ...p,
            mgrs_input: mgrsText,
            altitude: alt != null ? alt.toFixed(2) : p.altitude,
          }));

          setGpsAccuracy(acc);
          setGpsMsg(
            `GPS OK${acc != null ? ` (±${Math.round(acc)}m)` : ""} — filled MGRS.`,
          );

          parseAndApply(mgrsText, { autofillAltitude: true });
        } catch (e) {
          console.error(e);
          setGpsMsg("Failed to convert GPS to MGRS.");
        } finally {
          setGpsBusy(false);
        }
      },
      (err) => {
        console.error(err);
        setGpsBusy(false);

        if (err?.code === 1) {
          setGpsMsg("Permission denied. Enable location access.");
        } else if (err?.code === 2) {
          setGpsMsg("Position unavailable. Move outside or wait for GPS.");
        } else if (err?.code === 3) {
          setGpsMsg("Timeout getting GPS fix. Try again.");
        } else {
          setGpsMsg("GPS error. Try again.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  };

  const loadTak = async (e) => {
    setTakAnchor(e.currentTarget);
    setTakBusy(true);
    try {
      const r = await axios.get(`${backendUrl}/tak/markers`);
      setTakMarkers(r.data?.markers || []);
    } catch {
      setTakMarkers([]);
    } finally {
      setTakBusy(false);
    }
  };

  const pickTak = (m) => {
    setTakAnchor(null);
    try {
      const mgrs = toMGRS([Number(m.lon), Number(m.lat)], 5);
      const alt = m.alt_msl != null ? Number(m.alt_msl) : (m.hae != null ? Number(m.hae) : null);
      const text = alt != null ? `${mgrs} ${alt.toFixed(2)}m` : mgrs;
      clearErrors();
      setFormData((p) => ({ ...p, mgrs_input: text, altitude: alt != null ? alt.toFixed(2) : p.altitude }));
      parseAndApply(text, { autofillAltitude: true });
    } catch {
      setSubmitError("Could not convert the TAK marker position.");
    }
  };

  const handleSubmit = async () => {
    setResult("");
    clearErrors();

    if (!effectiveGunNumber) {
      setSubmitError("Select a gun first.");
      return;
    }

    const rawInput = (formData.mgrs_input || "").trim();
    const parsedNow = parseMgrsAndAltitude(rawInput);
    if (!parsedNow.ok) {
      setParsed({ mgrsNormalized: "", lat: null, lon: null });
      setMgrsError(parsedNow.error);
      return;
    }

    setParsed({
      mgrsNormalized: parsedNow.mgrs,
      lat: parsedNow.lat,
      lon: parsedNow.lon,
    });

    const altitudeCandidateStr =
      (formData.altitude || "").trim() ||
      (parsedNow.altitude != null ? String(parsedNow.altitude) : "");

    const altitudeNum = parseFloat(altitudeCandidateStr.replace(",", "."));
    const altErr = validateAltitudeMeters(altitudeNum);
    if (altErr) {
      setAltitudeError(altErr || "Invalid altitude.");
      return;
    }

    const payload = {
      gun_number: effectiveGunNumber,
      is_battery_center: formData.is_battery_center,
      Norths: Number(parsedNow.lat.toFixed(7)),
      wests: Number(parsedNow.lon.toFixed(7)),
      altitude: Number(altitudeNum.toFixed(2)),
    };

    try {
      const response = await axios.post(`${backendUrl}/latest-gun-position`, payload);
      setResult(JSON.stringify(response.data, null, 2));

      if (typeof onSaved === "function") {
        await onSaved();
      }

      setFormData((prev) => ({
        ...prev,
        gun_number: fixedGunNumber ? String(fixedGunNumber) : "",
        is_battery_center: false,
        mgrs_input: "",
        altitude: "",
      }));
      setParsed({ mgrsNormalized: "", lat: null, lon: null });
      setGpsAccuracy(null);
      setGpsMsg("");
    } catch (error) {
      setSubmitError(error?.response?.data?.message || error.message);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.6,
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.028)
          : alpha(theme.palette.common.black, 0.015),
    },
  };

  return (
    <Box
      sx={{
        ...tacticalCardSx(theme),
        borderRadius: 2.5,
        p: { xs: 1.5, sm: 1.8 },
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack spacing={0.45}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                Gun Position Report
              </Typography>
              {fixedGunNumber && (
                <Chip
                  size="small"
                  label={`Gun ${String(fixedGunNumber)} locked`}
                  sx={tacticalChipSx(theme, "warning")}
                />
              )}
              {formData.is_battery_center && (
                <Chip
                  size="small"
                  label="Battery center"
                  sx={tacticalChipSx(theme, "success")}
                />
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Report gun or battery center position using MGRS or live GPS.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label="Persistent position"
              sx={tacticalChipSx(theme, "neutral")}
            />
            <Chip
              size="small"
              label={
                effectiveGunNumber
                  ? `Gun ${effectiveGunNumber}`
                  : "Gun required"
              }
              sx={tacticalChipSx(
                theme,
                effectiveGunNumber ? "neutral" : "warning",
              )}
            />
          </Stack>
        </Stack>

        {!!submitError && <Alert severity="error">{submitError}</Alert>}

        {!!gpsMsg && (
          <Alert severity={gpsMsg.startsWith("GPS OK") ? "success" : "info"}>
            {gpsMsg}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gap: 1.1,
            gridTemplateColumns: { xs: "1fr", md: "1.15fr 1fr" },
          }}
        >
          <Box
            sx={{
              ...tacticalMetricSx(theme),
              display: "grid",
              gap: 1.15,
            }}
          >
            <Stack direction="row" spacing={0.8} alignItems="center">
              <PrecisionManufacturingIcon
                sx={{ fontSize: 18, color: "text.secondary" }}
              />
              <Typography sx={{ fontWeight: 600 }}>Assignment</Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.15}>
              {!hideGunSelect ? (
                <FormControl fullWidth size="small">
                  <InputLabel id="gun-number-label">Gun</InputLabel>
                  <Select
                    labelId="gun-number-label"
                    label="Gun"
                    value={formData.gun_number}
                    onChange={(e) => {
                      clearErrors();
                      setFormData((p) => ({
                        ...p,
                        gun_number: e.target.value,
                      }));
                    }}
                    disabled={false}
                    required
                    sx={fieldSx}
                  >
                    {gunOptions.length === 0 ? (
                      <MenuItem value="" disabled>
                        No guns available
                      </MenuItem>
                    ) : (
                      gunOptions.map((g) => (
                        <MenuItem key={g.value} value={g.value}>
                          {g.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Gun"
                  value={effectiveGunNumber ? `Gun ${effectiveGunNumber}` : ""}
                  InputProps={{ readOnly: true }}
                  disabled={false}
                  sx={fieldSx}
                />
              )}

              <Box
                sx={{
                  ...tacticalCardSx(
                    theme,
                    true,
                    formData.is_battery_center,
                    "success",
                  ),
                  px: 1.15,
                  py: 0.35,
                  minHeight: 40,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  sx={{ whiteSpace: "nowrap", m: 0 }}
                  control={
                    <Checkbox
                      checked={formData.is_battery_center}
                      onChange={(e) => {
                        clearErrors();
                        setFormData((p) => ({
                          ...p,
                          is_battery_center: e.target.checked,
                        }));
                      }}
                      disabled={false}
                      size="small"
                    />
                  }
                  label="Battery Center"
                />
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              ...tacticalMetricSx(theme),
              display: "grid",
              gap: 0.9,
            }}
          >
            <Stack direction="row" spacing={0.8} alignItems="center">
              <GpsFixedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography sx={{ fontWeight: 600 }}>Capture Status</Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={parsed.mgrsNormalized ? "Grid parsed" : "Grid pending"}
                sx={tacticalChipSx(
                  theme,
                  parsed.mgrsNormalized ? "success" : "warning",
                )}
              />
              <Chip
                size="small"
                label={
                  gpsAccuracy != null
                    ? `GPS ±${Math.round(gpsAccuracy)}m`
                    : "No GPS fix"
                }
                sx={tacticalChipSx(
                  theme,
                  gpsAccuracy != null ? "success" : "neutral",
                )}
              />
              <Chip
                size="small"
                label={
                  statusToneFromMessage(gpsMsg) === "success"
                    ? "Live fix ready"
                    : "Manual entry ready"
                }
                sx={tacticalChipSx(
                  theme,
                  statusToneFromMessage(gpsMsg) === "success"
                    ? "success"
                    : "neutral",
                )}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Use MGRS with embedded altitude or request a live GPS fix from the
              device.
            </Typography>
          </Box>
        </Box>

        <Divider
          flexItem
          sx={{ borderColor: alpha(theme.palette.divider, 0.8) }}
        />

        <Box
          sx={{
            display: "grid",
            gap: 1.15,
            gridTemplateColumns: { xs: "1fr", lg: "1.2fr 0.8fr" },
            alignItems: "start",
          }}
        >
          <Box sx={{ display: "grid", gap: 1.15 }}>
            <Box sx={tacticalMetricSx(theme)}>
              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <EditLocationAltIcon
                  sx={{ fontSize: 18, color: "text.secondary" }}
                />
                <Typography sx={{ fontWeight: 600 }}>Grid Entry</Typography>
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.15}
                alignItems={{ sm: "flex-start" }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Coordinates (MGRS)"
                  placeholder="29SND6264358593 167.53m msl"
                  value={formData.mgrs_input}
                  onChange={(e) => {
                    const v = e.target.value;
                    setResult("");
                    clearErrors();
                    setFormData((p) => ({ ...p, mgrs_input: v }));
                    setParsed({ mgrsNormalized: "", lat: null, lon: null });
                  }}
                  onBlur={handleMgrsBlur}
                  disabled={false}
                  error={!!mgrsError}
                  helperText={
                    mgrsError ||
                    `Example: 29SND6264358593${
                      gpsAccuracy != null
                        ? `  |  Last GPS: ±${Math.round(gpsAccuracy)}m`
                        : ""
                    }`
                  }
                  required
                  sx={fieldSx}
                />

                <Button
                  type="button"
                  variant="outlined"
                  disabled={gpsBusy}
                  onClick={handleUseGps}
                  startIcon={<MyLocationIcon />}
                  sx={{
                    ...tacticalActionSx(theme, "warning"),
                    minWidth: { xs: "100%", sm: 152 },
                    alignSelf: { xs: "stretch", sm: "flex-start" },
                  }}
                >
                  {gpsBusy ? "Acquiring GPS…" : "Use GPS"}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  disabled={takBusy}
                  onClick={loadTak}
                  sx={{
                    ...tacticalActionSx(theme),
                    minWidth: { xs: "100%", sm: 130 },
                    alignSelf: { xs: "stretch", sm: "flex-start" },
                  }}
                >
                  {takBusy ? "TAK…" : "From TAK"}
                </Button>
                <Menu anchorEl={takAnchor} open={Boolean(takAnchor)} onClose={() => setTakAnchor(null)}>
                  {takMarkers.length === 0 ? (
                    <MenuItem disabled>No positioned TAK markers</MenuItem>
                  ) : (
                    takMarkers.map((m) => (
                      <MenuItem key={m.uid} onClick={() => pickTak(m)} sx={{ fontFamily: "monospace", fontSize: 12.5 }}>
                        {(m.callsign || m.uid).slice(0, 22)} — {Number(m.lat).toFixed(4)}, {Number(m.lon).toFixed(4)}{m.alt_msl != null ? ` · ${Math.round(m.alt_msl)}m MSL` : ""}
                      </MenuItem>
                    ))
                  )}
                </Menu>
              </Stack>
            </Box>

            <Box sx={tacticalMetricSx(theme)}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Parsed Coordinates
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.15}>
                <TextField
                  fullWidth
                  size="small"
                  label="Norths"
                  value={parsed.lat != null ? parsed.lat.toFixed(7) : ""}
                  InputProps={{ readOnly: true }}
                  disabled={false}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Wests"
                  value={parsed.lon != null ? parsed.lon.toFixed(7) : ""}
                  InputProps={{ readOnly: true }}
                  disabled={false}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Normalized MGRS"
                  value={parsed.mgrsNormalized || ""}
                  InputProps={{ readOnly: true }}
                  disabled={false}
                  sx={fieldSx}
                />
              </Stack>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gap: 1.15 }}>
            <Box sx={tacticalMetricSx(theme)}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Altitude & Submit
              </Typography>
              <Stack spacing={1.15}>
                <TextField
                  fullWidth
                  size="small"
                  label="Altitude (m)"
                  value={formData.altitude}
                  onChange={(e) => {
                    setResult("");
                    clearErrors();
                    setFormData((p) => ({ ...p, altitude: e.target.value }));
                  }}
                  onBlur={() => {
                    const v = (formData.altitude || "").trim();
                    if (!v) return;
                    const n = parseFloat(v.replace(",", "."));
                    const err = validateAltitudeMeters(n);
                    setAltitudeError(err);
                  }}
                  disabled={false}
                  error={!!altitudeError}
                  helperText={altitudeError || " "}
                  required
                  sx={fieldSx}
                />

                <Button
                  type="button"
                  variant="contained"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                  endIcon={<SendIcon />}
                  sx={{
                    ...tacticalActionSx(theme, "success", "contained"),
                    width: "100%",
                  }}
                >
                  Submit Position
                </Button>
              </Stack>
            </Box>

            {result && (
              <Box sx={tacticalMetricSx(theme)}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Submission Result
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.2,
                    borderRadius: 1.6,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.85),
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.common.white, 0.03)
                        : alpha(theme.palette.common.black, 0.02),
                    color: "text.secondary",
                    fontSize: "0.78rem",
                    overflow: "auto",
                    maxHeight: 180,
                  }}
                >
                  {result}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

export default BatteryForm;
